import React, { useState, useEffect } from 'react';
import { X, Music, Loader2, Heart, List } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SoundCloudLibraryModal = ({ 
  isOpen, 
  onClose,
  // Crate context props
  isAddingToCrate,
  targetCrateId,
  targetCrateName,
  onTrackAddedToCrate,
  onReturnToCrate
}) => {
  const [activeTab, setActiveTab] = useState('likes'); // 'likes' or 'playlists'
  const [likes, setLikes] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addingTrackId, setAddingTrackId] = useState(null);

  const [soundcloudToken, setSoundcloudToken] = useState(localStorage.getItem('soundcloud_access_token'));

  // Function to refresh SoundCloud token
  const refreshSoundcloudToken = async () => {
    const refreshToken = localStorage.getItem('soundcloud_refresh_token');
    if (!refreshToken) {
      console.error('No SoundCloud refresh token available');
      return null;
    }
    
    try {
      const response = await fetch(
        `${API}/api/soundcloud/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.access_token) {
          localStorage.setItem('soundcloud_access_token', data.access_token);
          setSoundcloudToken(data.access_token);
          return data.access_token;
        }
      }
    } catch (err) {
      console.error('Failed to refresh SoundCloud token:', err);
      // Clear tokens if refresh fails
      localStorage.removeItem('soundcloud_access_token');
      localStorage.removeItem('soundcloud_refresh_token');
    }
    return null;
  };

  // Fetch likes
  const fetchLikes = async (token = soundcloudToken, isRetry = false) => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API}/api/soundcloud/me/likes?access_token=${token}&limit=50`
      );
      if (response.status === 401 && !isRetry) {
        console.log('SoundCloud token expired, attempting refresh...');
        const newToken = await refreshSoundcloudToken();
        if (newToken) {
          return fetchLikes(newToken, true);
        }
      }
      if (!response.ok) throw new Error('Failed to fetch likes');
      const data = await response.json();
      // Handle various response formats
      const trackList = Array.isArray(data) ? data : (data.collection || data.tracks || []);
      setLikes(trackList);
    } catch (err) {
      setError(err.message);
      setLikes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch playlists
  const fetchPlaylists = async (token = soundcloudToken, isRetry = false) => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API}/api/soundcloud/me/playlists?access_token=${token}&limit=50`
      );
      if (response.status === 401 && !isRetry) {
        console.log('SoundCloud token expired, attempting refresh...');
        const newToken = await refreshSoundcloudToken();
        if (newToken) {
          return fetchPlaylists(newToken, true);
        }
      }
      if (!response.ok) throw new Error('Failed to fetch playlists');
      const data = await response.json();
      // Handle various response formats
      const playlistList = Array.isArray(data) ? data : (data.collection || data.playlists || []);
      setPlaylists(playlistList);
    } catch (err) {
      setError(err.message);
      setPlaylists([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch playlist tracks
  const fetchPlaylistTracks = async (playlistId, token = soundcloudToken, isRetry = false) => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API}/api/soundcloud/playlists/${playlistId}/tracks?access_token=${token}`
      );
      if (response.status === 401 && !isRetry) {
        console.log('SoundCloud token expired, attempting refresh...');
        const newToken = await refreshSoundcloudToken();
        if (newToken) {
          return fetchPlaylistTracks(playlistId, newToken, true);
        }
      }
      if (!response.ok) throw new Error('Failed to fetch playlist tracks');
      const data = await response.json();
      // Handle various response formats
      const trackList = Array.isArray(data) ? data : (data.tracks || data.collection || []);
      setPlaylistTracks(trackList);
    } catch (err) {
      setError(err.message);
      setPlaylistTracks([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount and tab change
  useEffect(() => {
    if (isOpen && soundcloudToken) {
      if (activeTab === 'likes') {
        fetchLikes();
      } else {
        fetchPlaylists();
      }
    }
  }, [isOpen, activeTab, soundcloudToken]);

  // Handle adding track to crate
  const handleAddToCrate = async (track) => {
    if (!onTrackAddedToCrate) return;
    setAddingTrackId(track.id);
    try {
      // Fix SoundCloud artwork URL - replace size suffix with t500x500 for better quality
      let artworkUrl = track.artwork_url || track.user?.avatar_url;
      if (artworkUrl && artworkUrl.includes('sndcdn.com')) {
        // Replace -large or other sizes with -t500x500 for consistent sizing
        artworkUrl = artworkUrl.replace(/-large|-t\d+x\d+|-original|-badge|-small|-tiny/g, '-t500x500');
      }
      
      await onTrackAddedToCrate({
        id: track.id?.toString(),
        name: track.title,
        artist: track.user?.username || 'Unknown Artist',
        album_art: artworkUrl,
        duration_ms: track.duration,
        preview_url: track.stream_url || track.uri,
        soundcloud_stream_url: track.stream_url,
        external_url: track.permalink_url,
        source: 'soundcloud'
      });
    } catch (err) {
      console.error('Error adding track:', err);
    } finally {
      setAddingTrackId(null);
    }
  };

  if (!isOpen) return null;

  // Ensure currentTracks is always an array
  const currentTracks = selectedPlaylist 
    ? (Array.isArray(playlistTracks) ? playlistTracks : [])
    : (Array.isArray(likes) ? likes : []);

  return (
    <div 
      className="jam-modal-overlay" 
      onClick={onClose}
      style={{ zIndex: 100000 }}
    >
      {/* Gradient Border Wrapper */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          padding: '3px',
          borderRadius: '24px',
          background: 'linear-gradient(90deg, #ff5500, #ff00ff, #ff5500)',
          backgroundSize: '200% 100%',
          animation: 'gradientBorderSC 3s ease infinite',
          maxWidth: '1000px',
          width: '90%',
          maxHeight: '85vh',
          boxShadow: '0 0 30px rgba(255, 85, 0, 0.4), 0 0 60px rgba(255, 0, 255, 0.3)'
        }}
      >
        <style>{`
          @keyframes gradientBorderSC {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        
        <div style={{
          background: 'rgba(20, 20, 35, 0.98)',
          borderRadius: '21px',
          width: '100%',
          maxHeight: 'calc(85vh - 6px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(255, 85, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <img 
                src="/logos/soundcloud-logo.svg" 
                alt="SoundCloud" 
                style={{ width: 32, height: 32 }}
              />
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.3rem' }}>
                Your SoundCloud Library
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                padding: '0.5rem'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Crate Context Banner */}
          {isAddingToCrate && targetCrateName && (
            <div style={{
              padding: '0.75rem 2rem',
              background: 'rgba(0, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(0, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#00ffff',
                fontSize: '0.9rem'
              }}>
                📦 Adding to: <strong>{targetCrateName}</strong>
              </div>
              <button
                onClick={onReturnToCrate}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: '2px solid rgba(255, 0, 255, 0.6)',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: '#ff00ff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 0, 255, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 0, 255, 0.3)';
                }}
              >
                ← Return to Crate
              </button>
            </div>
          )}

          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            padding: '1rem 2rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <button
              onClick={() => {
                setActiveTab('likes');
                setSelectedPlaylist(null);
              }}
              style={{
                padding: '0.5rem 1rem',
                background: activeTab === 'likes' ? 'rgba(255, 85, 0, 0.2)' : 'transparent',
                border: `1px solid ${activeTab === 'likes' ? 'rgba(255, 85, 0, 0.5)' : 'rgba(255, 255, 255, 0.2)'}`,
                borderRadius: '6px',
                color: activeTab === 'likes' ? '#ff5500' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Heart size={16} /> Liked Tracks
            </button>
            <button
              onClick={() => {
                setActiveTab('playlists');
                setSelectedPlaylist(null);
              }}
              style={{
                padding: '0.5rem 1rem',
                background: activeTab === 'playlists' ? 'rgba(255, 85, 0, 0.2)' : 'transparent',
                border: `1px solid ${activeTab === 'playlists' ? 'rgba(255, 85, 0, 0.5)' : 'rgba(255, 255, 255, 0.2)'}`,
                borderRadius: '6px',
                color: activeTab === 'playlists' ? '#ff5500' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <List size={16} /> Playlists
            </button>
          </div>

          {/* Content */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '1rem 2rem'
          }}>
            {isLoading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem',
                color: 'rgba(255, 255, 255, 0.5)'
              }}>
                <Loader2 size={32} className="spinning" style={{ marginRight: '1rem' }} />
                Loading...
              </div>
            ) : error ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#f87171'
              }}>
                {error}
              </div>
            ) : activeTab === 'playlists' && !selectedPlaylist ? (
              // Playlists Grid
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                {playlists.map((playlist) => (
                  <div
                    key={playlist.id}
                    onClick={() => {
                      setSelectedPlaylist(playlist);
                      fetchPlaylistTracks(playlist.id);
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 85, 0, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(255, 85, 0, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    <div style={{
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: '0.75rem',
                      background: 'rgba(255, 85, 0, 0.1)'
                    }}>
                      {playlist.artwork_url ? (
                        <img 
                          src={playlist.artwork_url} 
                          alt="" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Music size={48} style={{ color: 'rgba(255, 85, 0, 0.5)' }} />
                        </div>
                      )}
                    </div>
                    <div style={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
                      {playlist.title}
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}>
                      {playlist.track_count || 0} tracks
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Track List (Likes or Playlist Tracks)
              <div>
                {selectedPlaylist && (
                  <button
                    onClick={() => setSelectedPlaylist(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#ff5500',
                      cursor: 'pointer',
                      marginBottom: '1rem',
                      fontSize: '0.9rem'
                    }}
                  >
                    ← Back to Playlists
                  </button>
                )}
                {currentTracks.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: 'rgba(255, 255, 255, 0.5)'
                  }}>
                    No tracks found
                  </div>
                ) : (
                  currentTracks.map((item) => {
                    const track = item.track || item;
                    return (
                      <div
                        key={track.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          marginBottom: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.02)',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'}
                      >
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '6px',
                          overflow: 'hidden',
                          flexShrink: 0,
                          background: 'rgba(255, 85, 0, 0.1)'
                        }}>
                          {track.artwork_url ? (
                            <img 
                              src={track.artwork_url} 
                              alt="" 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Music size={20} style={{ color: 'rgba(255, 85, 0, 0.5)' }} />
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {track.title}
                          </div>
                          <div style={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {track.user?.username || 'Unknown Artist'}
                          </div>
                        </div>
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.4)',
                          fontSize: '0.8rem'
                        }}>
                          {Math.floor(track.duration / 60000)}:{String(Math.floor((track.duration % 60000) / 1000)).padStart(2, '0')}
                        </div>
                        {isAddingToCrate && (
                          <button
                            onClick={() => handleAddToCrate(track)}
                            disabled={addingTrackId === track.id}
                            style={{
                              background: 'rgba(0, 255, 255, 0.1)',
                              border: '1px solid rgba(0, 255, 255, 0.4)',
                              borderRadius: '6px',
                              padding: '0.5rem 0.75rem',
                              color: '#00ffff',
                              cursor: addingTrackId === track.id ? 'not-allowed' : 'pointer',
                              fontSize: '1.2rem',
                              opacity: addingTrackId === track.id ? 0.5 : 1,
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                              if (addingTrackId !== track.id) {
                                e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (addingTrackId !== track.id) {
                                e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                                e.currentTarget.style.transform = 'scale(1)';
                              }
                            }}
                            title={`Add to ${targetCrateName}`}
                          >
                            {addingTrackId === track.id ? '⏳' : '📦'}
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoundCloudLibraryModal;
